import { api } from './apiClient';

/**
 * Thin clients for the modules added in migration 015.
 *
 * Grouped in one file rather than seven near-identical ones: these are
 * read-mostly calls for dashboard widgets and header chrome, and splitting
 * them would be seven files of four lines each.
 */

// ── Notifications ──────────────────────────────────────────────────────────
export interface NotificationItem {
  id: number;
  severity: 'CRITICAL' | 'IMPORTANT' | 'NORMAL' | 'INFO';
  category: string;
  title: string;
  body?: string | null;
  link?: string | null;
  /** Present when several notifications collapsed into one row. */
  count?: number;
  read_at?: string | null;
  created_at: string;
}

export const notificationService = {
  list: (params?: { unreadOnly?: boolean; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.unreadOnly) q.set('unreadOnly', 'true');
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return api.get(`/notifications${qs ? `?${qs}` : ''}`);
  },
  unreadCount: (): Promise<{ total: number; bySeverity: Record<string, number> }> =>
    api.get('/notifications/unread-count'),
  markRead: (id: number) => api.patch(`/notifications/${id}/read`, {}).then((r: any) => r?.data ?? r),
  markAllRead: () => api.post('/notifications/mark-all-read', {}),
  remove: (id: number) => api.delete(`/notifications/${id}`).then((r: any) => r?.data ?? r),
};

// ── Approvals ──────────────────────────────────────────────────────────────
export const approvalService = {
  pending: () => api.get('/approvals/pending'),
  summary: (): Promise<{ summary: Array<{ entity_type: string; count: number }>; total: number }> =>
    api.get('/approvals/summary'),
  requests: (params?: { status?: string; entityType?: string }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.entityType) q.set('entityType', params.entityType);
    const qs = q.toString();
    return api.get(`/approvals/requests${qs ? `?${qs}` : ''}`);
  },
  detail: (id: number) => api.get(`/approvals/requests/${id}`),
  approve: (id: number, comments?: string) => api.post(`/approvals/requests/${id}/approve`, { comments }),
  reject: (id: number, comments?: string) => api.post(`/approvals/requests/${id}/reject`, { comments }),
  cancel: (id: number) => api.post(`/approvals/requests/${id}/cancel`, {}),
};

// ── HR ─────────────────────────────────────────────────────────────────────
// The full typed client (departments, employees, attendance, leave, payroll,
// recruitment — everything the HR module pages use) lives in hrService.ts.
// Re-exported here so existing `import { hrService } from './workspaceService'`
// call sites keep working without a second, narrower client to drift out of sync.
export { hrService } from './hrService';

// ── Helpdesk ───────────────────────────────────────────────────────────────
export const helpdeskService = {
  summary: () => api.get('/helpdesk/summary'),
  tickets: (params?: { status?: string; overdueOnly?: boolean; limit?: number }) => {
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => v != null && q.set(k, String(v)));
    const qs = q.toString();
    return api.get(`/helpdesk/tickets${qs ? `?${qs}` : ''}`);
  },
};

// ── Calendar ───────────────────────────────────────────────────────────────
export const calendarService = {
  upcoming: (days = 7) => api.get(`/calendar/upcoming?days=${days}`),
  agenda: () => api.get('/calendar/agenda'),
  events: (from: string, to: string) =>
    api.get(`/calendar/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
};

// ── Analytics ──────────────────────────────────────────────────────────────
export interface SeriesPoint {
  date: string;
  /** null means no snapshot that day — render a gap, never interpolate. */
  value: number | null;
}

export interface MetricSeries {
  metricKey: string;
  days: number;
  points: SeriesPoint[];
  changePct: number | null;
  coverage: string;
}

export const analyticsService = {
  metrics: (): Promise<{ metrics: string[] }> => api.get('/analytics/metrics'),
  series: (metric: string, days = 30): Promise<MetricSeries> =>
    api.get(`/analytics/series?metric=${encodeURIComponent(metric)}&days=${days}`),
  seriesMulti: (metrics: string[], days = 30): Promise<{ series: MetricSeries[] }> =>
    api.get(`/analytics/series-multi?metrics=${metrics.join(',')}&days=${days}`),
  activityFeed: (params?: { limit?: number; sinceId?: number; module?: string }) => {
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => v != null && q.set(k, String(v)));
    const qs = q.toString();
    return api.get(`/analytics/activity-feed${qs ? `?${qs}` : ''}`);
  },
  snapshot: () => api.post('/analytics/snapshot', {}),
};
